import { computed, ref, Ref, watch } from '@vue/composition-api';
import { ListProps, RequiredProp } from './types';

/**
 * Generic Vue props that input related elements share
 */
export interface InputProps {
	value: RequiredProp<typeof String>;
	rules?: { required: false };
	label: { required: false; type: typeof String };
	id: RequiredProp<typeof String>;
	name: RequiredProp<typeof String>;
	min: { type: typeof String; required: false };
	max: { type: typeof String; required: false };
	error?: { type: typeof String; required: false };
	mode: { type: typeof String; default: 'aggressive' };
}
export const inputProps: InputProps = {
	value: {
		type: String,
		required: true,
	},
	rules: {
		required: false,
	},
	label: {
		type: String,
		required: false,
	},
	id: {
		type: String,
		required: true,
	},
	name: {
		type: String,
		required: true,
	},
	min: {
		type: String,
		required: false,
	},
	max: {
		type: String,
		required: false,
	},
	error: {
		type: String,
		required: false,
	},
	mode: {
		type: String,
		default: 'aggressive',
	},
};

export interface FormProps {
	action: {
		type: typeof String;
		required: true;
	};
	method: {
		type: typeof String;
		required: false;
	};
	recaptcha: {
		type: typeof Boolean;
		required: false;
	};
}

export const formProps: FormProps = {
	action: {
		type: String,
		required: true,
	},
	method: {
		type: String,
		required: false,
	},
	recaptcha: {
		type: Boolean,
		required: false,
	},
};

/**
 * Returns rules as object if given as string
 * @param rules
 */
const extractedRules = (rules: string | Record<string, string>) => {
	const isNumeric = (str: string) => !isNaN(parseFloat(str));

	if (typeof rules === 'object') return rules;

	const composedRules: Record<PropertyKey, any> = [];

	rules.split('|').forEach((rule: string) => {
		const parts = rule.split(':');

		if (parts.length === 1) {
			composedRules[parts[0]] = true;
			return;
		}

		const ruleParams = parts[1].split(',');

		if (ruleParams.length === 1)
			composedRules[parts[0]] = isNumeric(ruleParams[0]) ? parseInt(ruleParams[0]) : ruleParams[0];
		else composedRules[parts[0]] = ruleParams.map((param) => (isNumeric(param) ? parseInt(param) : param));
	});

	return composedRules;
};

export function useList(props: ListProps, emit: (event: string, ...args: any[]) => void) {
	const currentlySelectedOption = computed(() => props.options.find((option) => option.value === props.value));

	const selectValue = (value: string) => {
		const selectedOption = props.options.find((option) => option.value === value);

		if (!selectedOption) throw new Error(`Could not select option ${selectedOption}`);

		emit('input', value);
	};

	return {
		currentlySelectedOption,
		selectValue,
	};
}

/**
 * Returns a computed property which adds an asterisk to labels that are mandatory
 * @param props
 * @returns { ComputedRef }
 */
export function useDisplayLabel(props: { label?: string } & Partial<{ rules: any }>) {
	const displayLabel = computed(() => {
		if (!props.label) return '';
		if (!props.rules) return props.label;

		const rules = extractedRules(props.rules);

		if (rules.required || rules.required === '') return props.label + ' <span class="text-primary">*</span>';
		return props.label;
	});

	return { displayLabel };
}

/**
 * Implements a watcher on a given input ref or an object that contains a reactive object and a property to watch
 * and makes sure the value is clamped between a given min and max value
 *
 * @todo: implement proper clamping
 * @param inputValue
 * @param min
 * @param max
 */
export function useMinMaxWatcher(inputValue: any, min: number, max: number) {
	const isRef = inputValue.constructor.name === 'RefImpl';
	const watchSource = isRef ? () => inputValue : () => inputValue.object[inputValue.key];

	watch(watchSource, (value) => {
		const numberValue = parseInt(value);

		if (value === '-') return isRef ? (inputValue.value = '-') : (inputValue.object[inputValue.key] = '-');

		if (isNaN(numberValue) && value === '') return;

		if (isNaN(numberValue))
			return isRef ? (inputValue.value = String(min)) : (inputValue.object[inputValue.key] = String(min));
		else if (numberValue < min)
			return isRef ? (inputValue.value = String(min)) : (inputValue.object[inputValue.key] = String(min));
		else if (numberValue > max)
			return isRef ? (inputValue.value = String(max)) : (inputValue.object[inputValue.key] = String(max));

		return isRef
			? (inputValue.value = String(numberValue))
			: (inputValue.object[inputValue.key] = String(numberValue));
	});
}

export function getNextWeekday(date: Date): Date {
	const nextDay = new Date(new Date(date).setDate(date.getDate() + 1));
	if (nextDay.getDay() === 6 || nextDay.getDay() === 0) {
		return getNextWeekday(nextDay);
	}
	return nextDay;
}

export function getNextWeekdays(startingDate: Date, numberOfDays: number) {
	const dates = [getNextWeekday(startingDate)];
	for (let i = 0; i < numberOfDays - 1; i++) {
		dates.push(getNextWeekday(dates[i]));
	}
	return dates;
}
